<template>
  <SoAdminPage title="Marketing banners">
    <template #subtitle>
      <span data-testid="marketing-banners-intro">
        The marketing banners in My Account and the app, per page, in priority
        order (lower shows first).
        <template v-if="catalogue?.editable">
          Switching one on or off or editing it reaches customers within about a
          minute.
        </template>
        Being on is not a guarantee a banner shows: feature flags and
        eligibility rules decide that per customer.
      </span>
    </template>

    <div
      v-if="definitions.isPlaceholderData"
      class="d-flex justify-center my-12"
      data-testid="marketing-banners-loading"
    >
      <SoLoader />
    </div>

    <SoAlert
      v-else-if="!catalogue"
      type="error"
      title="Failed to load banner definitions"
      data-testid="marketing-banners-error"
    >
      <div class="mt-3">
        <SoButton
          type="secondary"
          title="Try again"
          :loading="definitions.isFetching"
          data-testid="marketing-banners-retry"
          @click="definitions.refetch()"
        />
      </div>
    </SoAlert>

    <template v-else>
      <SoAlert
        v-if="!catalogue.editable"
        type="info"
        title="Editing is not switched on yet, so these banners are read-only."
        class="mb-4"
        data-testid="marketing-banners-read-only"
      />
      <SoTextField
        v-model="search"
        label="Search title, body or name"
        clearable
        hide-details
        class="mb-4 search-field"
        data-testid="banner-search"
      />

      <SoTabs v-model="currentTab" divider data-testid="marketing-banners-tabs">
        <SoTab title="Banners">
          <SoWidget
            v-for="placement in placements"
            :key="placement.tag"
            class="mt-4"
            :data-testid="`placement-${placement.tag}`"
          >
            <MarketingBannerTable
              :title="placement.label"
              :editable="catalogue.editable"
              :rows="placement.ranked"
              :groups="placement.groups"
              :search="search ?? ''"
              :no-data-text="emptyText(placement.label)"
              :data-testid="`placement-${placement.tag}-ranked`"
              @edit="openEdit"
            />
            <div
              v-if="!catalogue.editable && placement.unranked.length"
              class="mt-6"
              :data-testid="`placement-${placement.tag}-unranked`"
            >
              <div class="text-subtitle-1 font-weight-bold">
                Not in priority list
              </div>
              <p class="text-caption text-medium-emphasis mb-2">
                Tagged {{ placement.label }} but missing from its priority list,
                so these never outrank the banners above.
              </p>
              <MarketingBannerTable
                :rows="placement.unranked"
                :groups="placement.groups"
              />
            </div>
          </SoWidget>
        </SoTab>
        <SoTab title="Alerts">
          <SoWidget class="mt-4">
            <template #title>Alerts</template>
            <AlertDefinitionsTable :alerts="alerts" />
          </SoWidget>
        </SoTab>
      </SoTabs>
    </template>
  </SoAdminPage>
  <MarketingBannerEditDialog v-model="showEdit" :group="editing" />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoLoader from "@/components/SoLoader.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoTabs from "@/components/tabs/SoTabs.vue"
  import SoTab from "@/components/tabs/SoTab.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import MarketingBannerTable from "./components/MarketingBannerTable.vue"
  import AlertDefinitionsTable from "./components/AlertDefinitionsTable.vue"
  import MarketingBannerEditDialog from "./modals/MarketingBannerEditDialog.vue"
  import { AlertsBannersService } from "@/services/admin/AlertsBannersService"
  import {
    IAlertDefinition,
    IAlertsBannersCatalogue,
    IBannerGroup,
    IBannerPlacement,
  } from "@/models/alertsBanners/alertsBanners.types"

  export default defineComponent({
    components: {
      SoAdminPage,
      SoAlert,
      SoButton,
      SoLoader,
      SoTextField,
      SoTabs,
      SoTab,
      SoWidget,
      MarketingBannerTable,
      AlertDefinitionsTable,
      MarketingBannerEditDialog,
    },
    data() {
      return {
        definitions: AlertsBannersService.getDefinitions(),
        search: "" as string | null,
        currentTab: 0,
        editing: null as IBannerGroup | null,
        showEdit: false,
      }
    },
    computed: {
      catalogue(): IAlertsBannersCatalogue | null {
        return this.definitions.data
      },
      placements(): IBannerPlacement[] {
        return this.catalogue?.placements(this.search ?? "") ?? []
      },
      alerts(): IAlertDefinition[] {
        return this.catalogue?.filteredAlerts(this.search ?? "") ?? []
      },
    },
    methods: {
      openEdit(group: IBannerGroup) {
        this.editing = group
        this.showEdit = true
      },
      emptyText(label: string): string {
        return this.search?.trim()
          ? `No ${label} banners match your search`
          : `No banners in the ${label} priority list`
      },
    },
  })
</script>
<style scoped>
  .search-field {
    max-width: 420px;
  }
</style>
